import {
  buildLearnedControlBinding,
  chooseLearnCandidate,
  getControlBindingFieldOptions,
  type ControlBindingFieldOption,
  type ControlBindingSpec,
} from "./controlBindings";
import {
  defaultControlPrefs,
  loadControlPrefs,
  normalizeAdamBindings,
  saveControlPrefs,
  type ControlPrefs,
} from "./controlPrefs";
import {
  attachParamBindings,
  type AdamControlBinding,
} from "./controlRouter";
import {
  controlRegistry,
  type ControlMessage,
} from "./controlRegistry";
import {
  startMidiBridge,
  type MidiBridgeConfig,
  type MidiBridgeStatus,
  type MidiPortOption,
} from "./midiBridge";
import { resolveParamAddress, isDiscreteParamKind } from "./paramAddresses";
import { StoreSubject } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";

export type BindingEditorState = {
  target: string;
  targetLabel: string;
  draft: ControlBindingSpec;
  fieldOptions: ControlBindingFieldOption[];
  /** True when opened from Learn (unsaved until Apply). */
  fromLearn: boolean;
};

export type ControlSessionState = {
  /** Machine-local MIDI prefs (localStorage). */
  prefs: ControlPrefs;
  /** Project-owned learn bindings (draft / Export) — not in controls prefs. */
  bindings: AdamControlBinding[];
  midiStatus: MidiBridgeStatus;
  midiError: string | null;
  midiInputs: MidiPortOption[];
  midiOutputs: MidiPortOption[];
  /** Param path currently being learned, or null. */
  learningTarget: string | null;
  learnStatus: string | null;
  bindingEditor: BindingEditorState | null;
};

const initial: ControlSessionState = {
  prefs: defaultControlPrefs(),
  bindings: [],
  midiStatus: "idle",
  midiError: null,
  midiInputs: [],
  midiOutputs: [],
  learningTarget: null,
  learnStatus: null,
  bindingEditor: null,
};

export const controlSessionStore = new StoreSubject<ControlSessionState>(initial);

function patchSession(partial: Partial<ControlSessionState>): void {
  controlSessionStore.next({ ...controlSessionStore.getValue(), ...partial });
}

let midiHandle: ReturnType<typeof startMidiBridge> | null = null;
let bindingsUnbind: (() => void) | null = null;
let learnUnsub: (() => void) | null = null;
let learnTimer: ReturnType<typeof setTimeout> | null = null;
let learnEvents: ControlMessage[] = [];
let started = false;
/** When set (by labRuntime), learn/edit writes flush into the project draft. */
let bindingsPersist: ((bindings: AdamControlBinding[]) => void) | null = null;

function persistMidi(prefs: ControlPrefs): void {
  saveControlPrefs(prefs);
  patchSession({ prefs });
}

function rebindAll(bindings: AdamControlBinding[]): void {
  bindingsUnbind?.();
  bindingsUnbind = attachParamBindings(bindings);
}

function restartMidi(config: MidiBridgeConfig): void {
  midiHandle?.stop();
  midiHandle = startMidiBridge(config, {
    onChange: state => {
      patchSession({
        midiStatus: state.status,
        midiError: state.error,
        midiInputs: state.inputs,
        midiOutputs: state.outputs,
      });
    },
  });
  patchSession({
    midiStatus: midiHandle.getStatus(),
    midiError: midiHandle.getError(),
    midiInputs: midiHandle.getInputs(),
    midiOutputs: midiHandle.getOutputs(),
  });
}

/** Boot MIDI prefs (call once from labRuntime). Bindings come from the project draft. */
export function startControlSession(): void {
  if (started) {
    return;
  }
  started = true;
  const prefs = loadControlPrefs();
  patchSession({ prefs });
  restartMidi(prefs.midi);
}

/** Wire project-draft persistence for binding mutations (Export / local draft). */
export function setControlBindingsPersistence(
  fn: ((bindings: AdamControlBinding[]) => void) | null,
): void {
  bindingsPersist = fn;
}

export function stopControlSession(): void {
  cancelLearn();
  closeBindingEditor();
  midiHandle?.stop();
  midiHandle = null;
  bindingsUnbind?.();
  bindingsUnbind = null;
  bindingsPersist = null;
  started = false;
}

export function setMidiBridgeConfig(partial: Partial<MidiBridgeConfig>): void {
  const prefs = controlSessionStore.getValue().prefs;
  const midi = { ...prefs.midi, ...partial };
  const next = { ...prefs, midi };
  persistMidi(next);
  restartMidi(midi);
}

export function setControlBindings(bindings: AdamControlBinding[]): void {
  const next = normalizeAdamBindings(bindings);
  patchSession({ bindings: next });
  rebindAll(next);
  bindingsPersist?.(next);
}

export function upsertControlBinding(entry: AdamControlBinding): void {
  const current = controlSessionStore.getValue().bindings;
  const target = entry.target;
  setControlBindings([
    ...current.filter(b => b.target !== target),
    entry,
  ]);
}

export function clearControlBinding(target: string): void {
  setControlBindings(
    controlSessionStore.getValue().bindings.filter(b => b.target !== target),
  );
}

export function cancelLearn(): void {
  learnUnsub?.();
  learnUnsub = null;
  if (learnTimer) {
    clearTimeout(learnTimer);
    learnTimer = null;
  }
  learnEvents = [];
  patchSession({ learningTarget: null, learnStatus: null });
}

/**
 * Arm learn for an Adam param path. Move a MIDI control within ~3s.
 */
export function startLearn(targetPath: string): void {
  const addr = resolveParamAddress(targetPath);
  if (!addr) {
    patchSession({ learnStatus: `Unknown target ${targetPath}` });
    return;
  }
  cancelLearn();
  learnEvents = [];
  patchSession({
    learningTarget: targetPath,
    learnStatus: `Move a MIDI control for ${addr.label}…`,
  });

  learnUnsub = controlRegistry.subscribe(message => {
    if (message.source === "ui" || message.source === "custom-ui") {
      return;
    }
    learnEvents.push(message);
  });

  learnTimer = setTimeout(() => {
    finishLearn(targetPath, addr.range);
  }, 3200);
}

function finishLearn(targetPath: string, outputRange: [number, number]): void {
  learnUnsub?.();
  learnUnsub = null;
  learnTimer = null;
  const candidate = chooseLearnCandidate(learnEvents);
  learnEvents = [];
  if (!candidate) {
    patchSession({
      learningTarget: null,
      learnStatus: "Learn timed out — no MIDI motion detected.",
    });
    return;
  }
  const addr = resolveParamAddress(targetPath);
  const binding = buildLearnedControlBinding(candidate, outputRange, {
    mode: addr && isDiscreteParamKind(addr.kind) ? "toggle" : "absolute",
  });
  const label = addr?.label ?? targetPath;
  upsertControlBinding({ target: targetPath, binding });
  patchSession({
    learningTarget: null,
    learnStatus: `Bound ${label} ← ${candidate.path}`,
  });
  openBindingEditor({
    target: targetPath,
    draft: binding,
    fromLearn: true,
  });
  getLabActions()?.setStatus?.(`Learned ${candidate.path} → ${targetPath}`);
}

export function openBindingEditor(options: {
  target: string;
  draft: ControlBindingSpec;
  fromLearn?: boolean;
}): void {
  const addr = resolveParamAddress(options.target);
  const fieldOptions = getControlBindingFieldOptions(options.draft.path, 4);
  patchSession({
    bindingEditor: {
      target: options.target,
      targetLabel: addr?.label ?? options.target,
      draft: {
        ...options.draft,
        outputRange: options.draft.outputRange ?? addr?.range,
      },
      fieldOptions,
      fromLearn: options.fromLearn === true,
    },
  });
}

/** Open the editor for an existing bound control. */
export function editControlBinding(target: string): void {
  const entry = controlSessionStore
    .getValue()
    .bindings.find(b => b.target === target);
  if (!entry) {
    return;
  }
  openBindingEditor({
    target,
    draft: { ...entry.binding },
    fromLearn: false,
  });
}

export function patchBindingEditorDraft(
  updater: (draft: ControlBindingSpec) => ControlBindingSpec,
): void {
  const editor = controlSessionStore.getValue().bindingEditor;
  if (!editor) {
    return;
  }
  const draft = updater(editor.draft);
  const pathChanged = draft.path !== editor.draft.path;
  const path = draft.path?.trim();
  patchSession({
    bindingEditor: {
      ...editor,
      draft,
      fieldOptions: pathChanged
        ? getControlBindingFieldOptions(draft.path, Math.max(4, editor.fieldOptions.length))
        : editor.fieldOptions,
    },
  });
  // Live-apply while editing — no separate confirm step.
  if (path) {
    upsertControlBinding({
      target: editor.target,
      binding: { ...draft, path },
    });
  }
}

export function closeBindingEditor(): void {
  patchSession({ bindingEditor: null });
}

/** @deprecated Prefer live patchBindingEditorDraft; kept for callers that just close. */
export function applyBindingEditor(): void {
  closeBindingEditor();
}
